import { __assign } from "tslib";
import React from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { FormProvider, Controller, useForm } from 'react-hook-form';
import NftReduxModule from '../redux/ModuleLoadable';
import Modal, { MODAL_FOOTER_BUTTON_SIZE, } from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import ModalFooterDivider from '@wix/da-ds/pkg/modals/ModalFooterDivider';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import DropdownInput from './_partials/DropdownInput';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import SelectionListItem from '@wix/da-ds/pkg/SelectionList/SelectionListItem';
import s from './NftLinkPopup.scss';
// NOTE: Needs to be kept in sync with lib.nft on the backend
var blockchainNames = [
    'ETHEREUM',
    'POLYGON',
    'SOLANA',
    'KLAYTN',
    'ARBITRUM',
    'OPTIMISM',
    'TEZOS',
    'FLOW',
    'PALM',
];
var marketplacesByBlockchain = {
    'ETHEREUM': 'OpenSea',
    'POLYGON': 'OpenSea',
    'SOLANA': 'OpenSea',
    'KLAYTN': 'OpenSea',
    'TEZOS': 'Rarible',
    'FLOW': 'Rarible',
    'PALM': "Nifty's",
    'ARBITRUM': 'Arbazaar',
    'OPTIMISM': 'Quixotic',
};
export var NftLinkPopup = function (_a) {
    var _b;
    var deviation = _a.deviation, nft = _a.nft, requestClose = _a.requestClose, updateNft = _a.updateNft, deleteNft = _a.deleteNft;
    var t = useTranslation().t;
    var initialKnownMarketplace = true, initialUnknownMarketplace = false;
    var unknownMarketplaceFilter = function (link) { return link.name === 'deviation_custom_marketplace_url'; };
    if (nft && nft.externalLinks.length) {
        var unknownMarketplaceLinks = nft.externalLinks.filter(unknownMarketplaceFilter);
        initialUnknownMarketplace = unknownMarketplaceLinks.length > 0;
        initialKnownMarketplace = (nft.externalLinks.length - unknownMarketplaceLinks.length) > 0;
    }
    var formContext = useForm({
        mode: 'onChange',
        defaultValues: {
            blockchainName: (nft === null || nft === void 0 ? void 0 : nft.blockchainName) || 'ETHEREUM',
            blockchainIdentifierPrimary: (nft === null || nft === void 0 ? void 0 : nft.blockchainIdentifierPrimary) || '',
            blockchainIdentifierSecondary: (nft === null || nft === void 0 ? void 0 : nft.blockchainIdentifierSecondary) || '',
            knownMarketplace: initialKnownMarketplace,
            unknownMarketplace: initialUnknownMarketplace,
            unknownMarketplaceLink: ((_b = nft === null || nft === void 0 ? void 0 : nft.externalLinks.filter(unknownMarketplaceFilter)[0]) === null || _b === void 0 ? void 0 : _b.url) || '',
        },
    });
    var handleSubmit = formContext.handleSubmit, setValue = formContext.setValue, _c = formContext.formState, errors = _c.errors, isValid = _c.isValid, watch = formContext.watch;
    var formValues = watch();
    var blockchainName = formValues.blockchainName, unknownMarketplace = formValues.unknownMarketplace, unknownMarketplaceLink = formValues.unknownMarketplaceLink;
    var renderFooterButtons = function () { return [
        nft && (React.createElement(TextButton, { size: "medium", variant: "destructive-red", key: "edit-delete", onClick: function () { return deleteNft(deviation, nft); } }, t('nft.linkPopup.button.delete'))),
        nft && React.createElement(ModalFooterDivider, { key: "divider" }),
        (React.createElement(TextButton, { size: "medium", variant: "main", key: "cancel", onClick: requestClose }, t('nft.linkPopup.button.cancel'))),
        (React.createElement(BrandedButton, { size: MODAL_FOOTER_BUTTON_SIZE, variant: "brand-green", key: "submit", disabled: !isValid, onClick: handleSubmit(function () { return updateNft(deviation, formValues, nft); }) }, nft
            ? t('nft.linkPopup.button.update')
            : t('nft.linkPopup.button.create'))),
    ]; };
    return (React.createElement(React.Fragment, null,
        React.createElement(NftReduxModule, null),
        React.createElement(FormProvider, __assign({}, formContext),
            React.createElement(Modal, { isOpen: true, onRequestClose: requestClose, showHeaderBorder: true, showFooterBorder: true, heading: nft
                    ? t('nft.linkPopup.title.edit')
                    : t('nft.linkPopup.title'), footerRight: renderFooterButtons() },
                React.createElement("div", { className: s['root'] },
                    React.createElement("p", null, t('nft.linkPopup.description')),
                    React.createElement(Controller, { name: "blockchainName", render: function (_a) {
                            var onChange = _a.onChange, value = _a.value;
                            return (React.createElement(DropdownInput, { wrapperClassName: classnames(s['form-input'], s['dropdown-item']), items: blockchainNames.map(function (name) {
                                    return {
                                        value: name,
                                        label: "".concat(name[0]).concat(name.slice(1).toLowerCase()),
                                    };
                                }), id: "blockchainName", label: t('nft.linkPopup.input.blockchainName.label'), value: value, onSelect: onChange }));
                        } }),
                    React.createElement(Controller, { name: "blockchainIdentifierPrimary", rules: { required: true }, render: function (_a) {
                            var onChange = _a.onChange, value = _a.value;
                            return (React.createElement(TextInput, { id: "blockchainIdentifierPrimary", name: "blockchainIdentifierPrimary", details: errors.blockchainIdentifierPrimary &&
                                    t('nft.linkPopup.error.blockchainIdentifierPrimary'), validationResult: errors.blockchainIdentifierPrimary && 'error', label: t('nft.linkPopup.input.blockchainIdentifierPrimary.label'), value: value, onChange: onChange, isRequired: true }));
                        } }),
                    blockchainName !== 'SOLANA' && (React.createElement(Controller, { name: "blockchainIdentifierSecondary", rules: { required: blockchainName !== 'SOLANA' }, render: function (_a) {
                            var onChange = _a.onChange, value = _a.value;
                            return (React.createElement(TextInput, { id: "blockchainIdentifierSecondary", name: "blockchainIdentifierSecondary", details: errors.blockchainIdentifierSecondary &&
                                    t('nft.linkPopup.error.blockchainIdentifierSecondary'), validationResult: errors.blockchainIdentifierSecondary && 'error', label: t('nft.linkPopup.input.blockchainIdentifierSecondary.label'), value: value, onChange: onChange, isRequired: true }));
                        } })),
                    React.createElement(Controller, { name: "knownMarketplace", render: function (_a) {
                            var onChange = _a.onChange, value = _a.value;
                            return (React.createElement(SelectionListItem, { className: s['select-item'], boldLabel: true, label: t('nft.linkPopup.input.knownMarketplace.label', {
                                    marketplace: marketplacesByBlockchain[blockchainName],
                                }), subLabel: t('nft.linkPopup.input.knownMarketplace.subLabel', {
                                    marketplace: marketplacesByBlockchain[blockchainName],
                                }), control: "checkbox", isSelected: !!value, onChange: onChange }));
                        } }),
                    React.createElement(Controller, { name: "unknownMarketplace", render: function (_a) {
                            var onChange = _a.onChange, value = _a.value;
                            return (React.createElement(SelectionListItem, { boldLabel: true, label: t('nft.linkPopup.input.unknownMarketplace.label'), subLabel: t('nft.linkPopup.input.unknownMarketplace.subLabel'), control: "checkbox", isSelected: !!value || !!unknownMarketplaceLink, onChange: onChange, bottomSlot: (React.createElement(Controller, { name: "unknownMarketplaceLink", rules: {
                                        // Lifted a URL regex, as ain't nobody got time for that
                                        required: !!unknownMarketplace,
                                        pattern: /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_+.~#?&/=]*)$/,
                                        minLength: 7,
                                        maxLength: 256,
                                    }, render: function (_a) {
                                        var onUrlChange = _a.onChange, urlValue = _a.value;
                                        return (React.createElement(TextInput, { className: s['custom-url'], id: "unknownMarketplaceLink", name: "unknownMarketplaceLink", details: errors.unknownMarketplaceLink &&
                                                t('nft.linkPopup.error.unknownMarketplaceLink'), validationResult: errors.unknownMarketplaceLink && 'error', label: t('nft.linkPopup.input.unknownMarketplaceLink.label'), isLabelHidden: true, value: urlValue, onChange: function (e) {
                                                setValue('unknownMarketplace', true);
                                                onUrlChange(e);
                                            } }));
                                    } })) }));
                        } }))))));
};
NftLinkPopup.displayName = 'NftLinkPopup';
export default NftLinkPopup;
//# sourceMappingURL=NftLinkPopup.js.map